import { Container } from '@acies/core';
import Label from './label';
import theme from './theme';

/** The KeyboardShortcut Component, is used to indicate if a Component has a Keyboard shortcut that can be pressed, instead of clicking the Component with a pointer device. */
export default class KeyboardShortcut extends Container {
	/** @param value The text you want to display as the keyboars shortcut, example "ESC" for the Escape key. */
	public set text(value: string) {
		if (this.#text === value) {
			return;
		}
		assertIsString(value);
		this.#text = value;
		this.#textChanged = true;
		this.invalidateProperties();
	}

	public get text(): string {
		return this.#text;
	}

	/** The variant property controls which KeyboardShortcut styles you want to apply to the KeyboardShortcut. */
	public set variant(value: 'default' | 'disabled') {
		if (this.#variant === value) {
			return;
		}
		assertIsValidVariant(value);
		this.#variant = value;
		this.#variantChanged = true;
		this.invalidateProperties();
	}

	public get variant(): 'default' | 'disabled' {
		return this.#variant;
	}

	#label: Label;
	#text: string;
	#textChanged: boolean;
	#variant: 'default' | 'disabled';
	#variantChanged: boolean;

	public constructor() {
		super();
		this.cornerRadius = 4;
		this.height = 20;
		this.minWidth = 20;
		this.paddingHorizontal = 4;
		this.stroke = theme.strokes.soft;
		this.alignment = 'center';
		this.#label = new Label();
		this.#label.size = 'small';
		this.#text = '';
		this.#textChanged = false;
		this.#variant = 'default';
		this.#variantChanged = false;
	}

	protected override commitProperties(): void {
		super.commitProperties();
		if (this.#textChanged) {
			this.#commitText();
		}
		if (this.#variantChanged) {
			this.#commitVariant();
		}
	}

	protected override initialize(): void {
		super.initialize();
		this.addComponent(this.#label);
	}

	#commitText(): void {
		this.#textChanged = false;
		this.#label.text = this.text;
	}

	#commitVariant(): void {
		this.#variantChanged = false;
		this.#label.variant = this.variant;
	}
}
customElements.define('ac-keyboard-shortcut', KeyboardShortcut);

function assertIsString(value: unknown): asserts value is string {
	if (typeof value === 'string') {
		return;
	}
	throw new TypeError(`[${String(value)}] is invalid, must be of type string`);
}

function assertIsValidVariant(value: unknown): asserts value is 'default' | 'disabled' {
	if (value === 'default' || value === 'disabled') {
		return;
	}
	throw new TypeError(`[${String(value)}] is invalid, must be either "default" or "disabled"`);
}
